import { useWatch } from 'react-hook-form';
import React, { useMemo } from 'react';
import type { DataTableRenderCellParams, DataTableRowEventHandler } from '@/components/custom/erp/data-table-v2';
import type { ButtonType } from '@/components/custom/erp/input-table-v2';
import { EditableTableWithContext } from '../EditableTableWithContext';
import { FormFieldActions, FormFieldState } from '../../../hooks';
import type { RepeatRowsPayload } from '@/components/custom/erp';
import { SelectedCellInfo } from '../hooks/useTaxRows';
import { getTaxTableColumns } from './columns';
import { FormMode } from '@/types/form';

interface TaxTabProps {
  formMode: FormMode;
  rows: { uuid?: string | null }[];
  selectedRowUuid?: string | null;
  selectedCell?: SelectedCellInfo | null;
  state: {
    state: FormFieldState;
    actions: FormFieldActions;
  };
  onRowClick?: DataTableRowEventHandler<MuaHangRecord>;
  onCellClick?: (params: DataTableRenderCellParams<MuaHangRecord>) => void;
  onAddRow: () => void;
  onDeleteRow: () => void;
  onCopyRow: () => void;
  onPasteRow: (clipboardText?: string) => void | Promise<void>;
  onMoveRow: (direction: 'up' | 'down') => void;
  onRepeatForRows: (payload: RepeatRowsPayload<MuaHangRecord>) => void;
  onCellValueChange: (rowUuid: string, field: string, newValue: MuaHangRecord) => void;
}

const TAX_ACTION_ORDER: ButtonType[] = ['add', 'delete', 'copy', 'paste', 'moveUp', 'moveDown', 'export', 'pin'];
const noop = () => {};

export const TaxTab: React.FC<TaxTabProps> = ({
  formMode,
  rows,
  selectedRowUuid,
  selectedCell,
  state,
  onRowClick,
  onCellClick,
  onAddRow,
  onDeleteRow,
  onCopyRow,
  onPasteRow,
  onMoveRow,
  onRepeatForRows,
  onCellValueChange
}) => {
  const [dien_giai] = useWatch({ name: ['dien_giai'] });
  const columns = useMemo(
    () => getTaxTableColumns(state, dien_giai, onCellValueChange),
    [dien_giai, onCellValueChange, state]
  );
  const tableActions = useMemo(
    () => ({
      add: onAddRow,
      delete: onDeleteRow,
      copy: onCopyRow,
      paste: onPasteRow,
      moveUp: () => onMoveRow('up'),
      moveDown: () => onMoveRow('down'),
      export: noop,
      pin: noop
    }),
    [onAddRow, onCopyRow, onDeleteRow, onMoveRow, onPasteRow]
  );

  return (
    <EditableTableWithContext
      formMode={formMode}
      rows={rows}
      onRowClick={onRowClick}
      onCellClick={onCellClick}
      selectedRowId={selectedRowUuid || undefined}
      selectedCell={selectedCell}
      columns={columns}
      onRepeatForRows={onRepeatForRows}
      actions={tableActions}
      actionOrder={TAX_ACTION_ORDER}
    />
  );
};

export default TaxTab;
